<template>
    <div class="modal-overlay">
      <div class="modal-container">
        <div class="modal-header">
          <h3>{{ title }}</h3>
        </div>
        <div class="modal-body">
          <div class="modal-row">
            <div class="modal-input">
              <label>Title *</label>
              <input 
                v-model="newExerciseName"
                :class="{'error': newExerciseNameError}"
                type="text" 
                class="title" 
                placeholder="Title"/>
            </div>
          </div>
          <div class="modal-row">
            <div class="modal-input">
              <label>Primary *</label>
              <select 
                v-model="newExerciseMuscle" 
                @input="newExerciseMuscle = $event.target.value"
                :class="{'error': newExerciseMuscleError}">
                  <option value="">Select primary muscle</option>
                  <option v-for="muscle in muscleList" :key="muscle['value']" :value="muscle['value']">{{muscle['label']}}</option>
              </select>
              <ul v-if="newExerciseMuscle">
                <li>
                  {{newExerciseMuscle}}
                  <i class="fas fa-times" @click="newExerciseMuscle = ''"></i>
                </li>
              </ul>
            </div>
            <div class="modal-input">
              <label>Secondary</label>
              <select v-model="tempSecondaryMuscle" @input="addSecondaryMuscle">
                <option value="">Select secondary muscle</option>
                <option v-for="muscle in muscleList" :key="muscle['value']" :value="muscle['value']">{{muscle['label']}}</option>
              </select>
              <ul v-if="newExerciseSecondaryMuscles.length">
                <li v-for="muscle in newExerciseSecondaryMuscles" :key="muscle">
                  {{muscle}}
                  <i class="fas fa-times" @click="removeSecondaryMuscle(muscle)"></i>
                </li>
              </ul>
            </div>
            <div class="modal-input"></div>
          </div>
          <div class="modal-row">
            <div class="modal-input">
              <label>Equipment *</label>
              <select 
                v-model="newExerciseEquipment"
                :class="{'error': newExerciseEquipmentError}">
                  <option value="">Select equipment</option>
                  <option v-for="equip in equipment" :key="equip['value']" :value="equip['value']">{{equip['label']}}</option>
              </select>
            </div>
            <div class="modal-input">
              <label>Type *</label>
              <select 
                v-model="newExerciseType"
                :class="{'error': newExerciseTypeError}">
                  <option value="">Select type</option>
                  <option value="Cardio">Cardio</option>
                  <option value="Workout">Workout</option>
                  <option value="Legs">Workout - Legs</option>
                  <option value="Push">Workout - Push</option>
                  <option value="Pull">Workout - Pull</option>
              </select>
            </div>
            <div class="modal-input">
              <label>Difficulty *</label>
              <select 
                v-model="newExerciseDifficulty"
                :class="{'error': newExerciseDifficultyError}">
                  <option value="">Select difficulty</option>
                  <option value="beginner">Beginner</option>
                  <option value="intermediate">Intermediate</option>
                  <option value="advanced">Advanced</option>
              </select>
            </div>
          </div>
          <div class="modal-row">
            <div class="modal-input">
              <label>Description *</label>
              <textarea
                v-model="newExerciseDescription"
                placeholder="Description"
                :class="{'error': newExerciseDescriptionError}"/>
            </div>
          </div>
          <div class="modal-row">
            <div class="modal-input">
              <label>Keywords (separated with a comma) </label>
              <textarea v-model="newExerciseKeywords" type="text" class="keywords" placeholder="Keywords" />
            </div>
          </div>
          <div class="modal-row">
            <div class="video-input">
              <p>Male Video:</p>
              <div class="video-option dropbox default">
                <input
                  type="file"
                  @change="
                    filesChange($event.target.files, 'maleVideo');
                    fileCount = $event.target.files.length;
                  "
                  accept="video/mp4"
                  class="input-file"
                />
                <div v-if="!maleVideo && !maleVideoThumbnailUrl" class="default-vid">
                  <i class="fas fa-play"></i>
                </div>
                <div class="preview-vid" v-else>
                  <i class="fas fa-times" @click="deleteMaleVideo = true; maleVideoThumbnailUrl = ''; maleVideo = null;"></i>
                  <video
                    autoplay playsinline
                    :key="maleVideoThumbnailUrl"
                    width="320" height="240" controls>
                      <source :src="maleVideoThumbnailUrl" type="video/mp4">
                      Your browser does not support the video tag.
                  </video>
                </div>
              </div>
            </div>

            <div class="video-input">
              <p>Female Video:</p>
              <div class="video-option dropbox default">
                <input
                  type="file"
                  @change="
                    filesChange($event.target.files, 'femaleVideo');
                    fileCount = $event.target.files.length;
                  "
                  accept="video/mp4"
                  class="input-file"
                />
                <div v-if="!femaleVideo && !femaleVideoThumbnailUrl" class="default-vid">
                  <i class="fas fa-play"></i>
                </div>              
                <div class="preview-vid" v-else>
                  <i class="fas fa-times" @click="deleteFemaleVideo = true; femaleVideoThumbnailUrl = ''; femaleVideo = null;"></i>
                  <video
                    autoplay playsinline
                    :key="femaleVideoThumbnailUrl"
                    width="320" height="240" controls>
                      <source :src="femaleVideoThumbnailUrl" type="video/mp4">
                      Your browser does not support the video tag.
                  </video>
                </div>
              </div>
            </div>
          </div>

          <div class="modal-row">
            <div class="modal-input">
              <label>Assisted exercise:</label>
              <ToggleButton @change="newExerciseAssisted = !newExerciseAssisted" :value="editExerciseItem.assisted" />
            </div>
          </div>

          <div class="modal-row">
            <div class="modal-input">
              <label>Timer/set exercise:</label>
              <ToggleButton @change="newExerciseTimerSets = !newExerciseTimerSets" :value="editExerciseItem.timer_sets" />
            </div>
          </div>

          <div class="buttons">
            <button class="secondary-btn-dark" @click="closeModal();">
              DISCARD
            </button>
            <button @click="saveCreateExercise()" class="primary-btn">
              {{ mode === 'edit' ? 'SAVE CHANGES' : 'CREATE EXERCISE' }}
            </button>
          </div>
        </div>
      </div>
    </div>
</template>

<script>
import { createExercise, loadVideos, updateExercise } from '@/api/requests';
import { MUSCLES } from '@/constants';
import { ToggleButton } from 'vue-js-toggle-button'

export default {
  name: "AddExerciseModal",

  props: {
    equipment: {
      type: Array,
      default: () => []
    }
  },

  data() {
    return {
      newExerciseName: '',
      newExerciseDifficulty: '',
      newExerciseType: '',
      newExerciseKeywords: '',
      newExerciseDescription: '',
      newExerciseMuscle: '',
      newExerciseEquipment: '',
      newExerciseSecondaryMuscles: [],
      newExerciseAssisted: false,
      newExerciseTimerSets: false,
      tempSecondaryMuscle: '',

      maleVideo: null,
      maleVideoThumbnailUrl: '',
      deleteMaleVideo: false,
      femaleVideo: null,
      femaleVideoThumbnailUrl: '',
      deleteFemaleVideo: false,

      newExerciseNameError: false,
      newExerciseMuscleError: false,
      newExerciseEquipmentError: false,
      newExerciseTypeError: false,
      newExerciseDifficultyError: false,
      newExerciseDescriptionError: false,
    }
  },

  components: {
    ToggleButton
  },

  mounted() {
    if (this.mode === 'edit') {
      this.loadVideos(this.editExerciseItem.id);

      this.newExerciseName = this.editExerciseItem.title;
      this.newExerciseMuscle = this.mapMuslce(this.editExerciseItem.muscles);
      this.newExerciseEquipment = this.editExerciseItem.equipment;
      this.newExerciseType = this.editExerciseItem.exercise_type;
      this.newExerciseDifficulty = this.editExerciseItem.difficulty;
      this.newExerciseDescription = this.editExerciseItem.exercise_details;
      this.newExerciseKeywords = this.editExerciseItem.keywords;
      this.newExerciseAssisted = this.editExerciseItem.assisted;
      this.newExerciseTimerSets = this.editExerciseItem.timer_sets;
      if (this.newExerciseKeywords) {
        this.newExerciseKeywords = this.newExerciseKeywords.replace("{", "");
        this.newExerciseKeywords = this.newExerciseKeywords.replace("}", "");
        this.newExerciseKeywords = this.newExerciseKeywords.replaceAll("\"", "");
        this.newExerciseKeywords = this.newExerciseKeywords.replaceAll("\\", "");
      }

      if (this.editExerciseItem.secondary_muscles && this.editExerciseItem.secodary_muscles !== "") {
        const secondaryMuscles = JSON.parse(this.editExerciseItem.secondary_muscles);
        this.newExerciseSecondaryMuscles = secondaryMuscles.map(muscle => this.mapMuslce(muscle.label));
      }
    }
  },

  methods: {
    validateCreateExercise() {
      let valid = true;
      this.newExerciseNameError = false;
      this.newExerciseTypeError = false;
      this.newExerciseMuscleError = false;
      this.newExerciseEquipmentError = false;
      this.newExerciseDifficultyError = false;
      this.newExerciseDescriptionError = false;
      
      if (this.newExerciseName == '') {
        valid = false;
        this.newExerciseNameError = true;
      }
      if (this.newExerciseType == '') {
        valid = false;
        this.newExerciseTypeError = true;
      }
      if (this.newExerciseMuscle == '') {
        valid = false;
        this.newExerciseMuscleError = true;
      }
      if (this.newExerciseEquipment == '') {
        valid = false;
        this.newExerciseEquipmentError = true;
      }
      if (this.newExerciseDifficulty == '') {
        valid = false;
        this.newExerciseDifficultyError = true;
      }
      // TODO: Commenting this check out as its super annoying at the moment.
      // if (this.newExerciseDescription == '') {
      //   valid = false;
      //   this.newExerciseDescriptionError = true;
      // }

      return valid;
    },

    closeModal() {
      this.createExercise = false;
      this.$store.dispatch('exercise/closeExerciseModal');
    },

    resetCreateExercise() {
      this.newExerciseName = '';
      this.newExerciseType = '';
      this.newExerciseMuscle = '';
      this.newExerciseSecondaryMuscles = [];
      this.newExerciseEquipment = '';
      this.newExerciseKeywords = '';
      this.newExerciseDescription = '';
      this.addExercise = false;
      this.newExerciseAssisted = false;
      this.newExerciseTimerSets = false;
    },

    filesChange(fileList, type) {
      if (type === 'maleVideo') {
        this.maleVideo = fileList[0];
        this.deleteMaleVideo = false;
      } else if (type === 'femaleVideo') {
        this.femaleVideo = fileList[0];
        this.deleteFemaleVideo = false;
      }
    },

    saveCreateExercise() {
      if (this.validateCreateExercise()) {
        let payload = {
          title: this.newExerciseName,
          exercise_type: this.newExerciseType,
          equipment: this.newExerciseEquipment,
          difficulty: this.newExerciseDifficulty,
          description: this.newExerciseDescription || '',
          primary: [this.newExerciseMuscle],
          secondary: this.newExerciseSecondaryMuscles,
          keywords: this.newExerciseKeywords?.split('\n').filter(keyword => keyword !== '') || [],
          delete_male_video: this.deleteMaleVideo,
          delete_female_video: this.deleteFemaleVideo,
          assisted: this.newExerciseAssisted,
          timer_sets: this.newExerciseTimerSets,
        };

        let formData = new FormData();
        for (var key in payload) {
          const arrayValues = ['primary', 'secondary', 'keywords'];
          if (arrayValues.includes(key)) {
            for (var i = 0; i < payload[key].length; i++) {
              formData.append(`${key}[]`, payload[key][i]);
            }
          } else {
            formData.append(key, payload[key]);
          }
        }

        if (this.maleVideo != null) {
          formData.append(
            "male_video",
            this.maleVideo,
            this.maleVideo.name
          );
        }
        if (this.femaleVideo != null) {
          formData.append(
            "female_video",
            this.femaleVideo,
            this.femaleVideo.name
          );
        }

        if (this.mode === 'edit') {
          updateExercise(this.editExerciseItem.id, formData).then(() => {
            this.resetCreateExercise();
            this.$toasted.global.app_success({
              message: "Exercise has been updated."
            });
            this.$store.dispatch('reloadTable');
            this.closeModal();
          });
        } else {
          createExercise(formData).then(() => {
            this.resetCreateExercise();
            this.$toasted.global.app_success({
              message: "Exercise has been created."
            });
            this.createExercise = false;
            this.$store.dispatch('reloadTable');
          });
        }
      } else {
        this.$toasted.global.app_error({
          message: "Please fill in all required fields."
        });
      }
    },

    addSecondaryMuscle($event) {
      if (!this.newExerciseSecondaryMuscles.includes($event.target.value)) {
        this.newExerciseSecondaryMuscles.push($event.target.value); 
      }
      this.tempSecondaryMuscle = '';
    },

    removeSecondaryMuscle(muscle) {
      const index = this.newExerciseSecondaryMuscles.indexOf(muscle);
      this.newExerciseSecondaryMuscles.splice(index, 1);
      this.tempSecondaryMuscle = '';
    },

    mapMuslce(muscle) {
      const currentMuscle = MUSCLES.find(checkMuscle => checkMuscle.label == muscle);
      return currentMuscle.value;
    },

    loadVideos(id) {
      loadVideos(id).then(response => {
        if (response.data?.male_video) {
          this.maleVideoThumbnailUrl = this.url + response.data?.male_video
        }
        if (response.data?.female_video) {
          this.femaleVideoThumbnailUrl = this.url + response.data?.female_video
        }
      });
    },

  },

  computed: {
    muscleList() {
      return MUSCLES;
    },

    createExercise: {
      get () {
        return this.$store.state.exercise.createExercise
      },
      set (value) {
        this.$store.commit('exercise/updateAttribute', {value: value, type: 'createExercise'})
      }
    },

    mode() {
      return this.$store.state.exercise.editExercise.title ? 'edit' : 'create';
    },

    editExerciseItem() {
      return this.$store.state.exercise.editExercise;
    },

    title() {
      if (this.mode === 'create') {
        return 'CREATE NEW EXERCISE';
      } else {
        return 'EDIT: ' + this.editExerciseItem.title;
      }
    },

    url() {
      let url = window.location.origin;
      if (url === "http://localhost:8080") {
        url = "http://localhost:3000";
      }
      return url;
    },
  }
}
</script>

<style lang="scss" scoped>
@import "@/assets/css/variables";
@import "@/assets/css/internal";

.modal-overlay {
  position: fixed;
  top: 0;
  width: 100%;
  height: 100%;
  background: rgba(0,0,0,0.50);
  z-index: 100;
  left: 0;

  .modal-container {
    background: $sec0;
    width: 50%;
    margin-left: 25%;
    margin-top: 50px;
    box-shadow: 0px 0px 40px rgba(0, 0, 0, 0.55);
    max-height: 80%;
    overflow-y: scroll;

    .modal-header {
      padding: $spacing-base;
      
      h3 {
        margin: 0;
        padding: 0;
        color: $sec6;
        text-transform: uppercase;
      }
    }

    .modal-body {
      background: $sec1;
      padding: $spacing-base;

      .modal-row {
        display: flex;
        margin-bottom: $spacing-base;
        gap: $spacing-small;
        
        label {
          display: block;
          color: $sec7;
          font-size: $font-size-base;
          margin-bottom: 2px;
        }

        .modal-input {
          width: 100%;
          
          .title {
            width: 45%;
          }
          
          .keywords {
            height: 40px;
          }

          select {
            width: 100%;
          }

          textarea {
            width: 100%;
            box-sizing: border-box;
            resize: none;
          }

          ul {
            list-style: none;
            padding: 0;
            margin: 0;
            margin-top: 8px;

            li {
              background: $sec0;
              color: $sec5;
              float: left;
              font-size: $font-size-small;
              border-radius: 100px;
              padding: 2px 9px;
              justify-content: center;
              display: flex;
              align-items: center;
              margin-right: 4px;
              margin-bottom: 4px;

              i {
                padding: 3px;
                cursor: pointer;
              }
            }
          }
        }
      }
    }
  }
}

.video-option {
  margin-left: 0;
}

.video-input {
  flex: 0;

  p {
    font-weight: 500;
    font-size: 14px;
    color: #BDBDBD;
    padding: 0;
    margin: 0;
    margin-bottom: 4px;
  }
}

.dropbox {
  border-radius: 0;
  width: 130px;
  height: 130px;
  border: 1px solid $sec2;
}

.default {
  float: left;
  background: $sec0;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;

  img {
    width: 20px;
    height: 20px;
    display: inline-block;
  }
}
.default-vid { 
  display: flex;
  align-items: center;
  justify-content: center;

  i {
    font-size: $font-size-base;
    color: $sec4;
    font-weight: bold;
  }
}

.preview-vid {
  height: 130px;
  width: 130px;
  position: relative;
  display: flex;
  justify-content: center;

  i {
    right: 5px;
    position: absolute;
    z-index: 1;
    top: 5px;
  }
}

#preview {
  width: 100%;
  height: 100%;
}
</style>